import _ from 'lodash';
import Api from 'services/Api';
import {averageChartData, totalChartData, trendsChartData} from 'store/modules/shared/chartData';

const mutationTypes = {
    UPDATE_AVERAGE_RESERVES_CHART_DATA: 'UPDATE_AVERAGE_RESERVES_CHART_DATA',
    UPDATE_TOTAL_RESERVES_CHART_DATA: 'UPDATE_TOTAL_RESERVES_CHART_DATA',
    UPDATE_TRENDS_IN_RESERVES_CHART_DATA: 'UPDATE_TRENDS_IN_RESERVES_CHART_DATA',
    UPDATE_AVERAGE_RESERVES_TYPE: 'UPDATE_AVERAGE_RESERVES_TYPE',
    UPDATE_TOTAL_RESERVES_TYPE: 'UPDATE_TOTAL_RESERVES_TYPE',
    UPDATE_TRENDS_IN_RESERVES_TYPE: 'UPDATE_TRENDS_IN_RESERVES_TYPE',
    UPDATE_TRENDS_IN_RESERVES_PERIOD: 'UPDATE_TRENDS_IN_RESERVES_PERIOD',
    START_RUNNING: 'START_RUNNING',
    STOP_RUNNING: 'STOP_RUNNING',
    UPDATE_FILTER: 'UPDATE_FILTER'
};

/**
 * Template for average and total reserves
 */
function chartTemplate() {
    return {
        number: {
            chartData: {}
        },
        value: {
            chartData: {}
        }
    };
}

function trendTemplate() {
    return {
        total: {
            data: {}
        },
        average: {
            data: {}
        },
        number: {
            data: {}
        }
    };
}

/**
 * Template for a trends in reserves
 */
function trendChartTemplate() {
    return {
        month: trendTemplate(),
        quarter: trendTemplate(),
        year: trendTemplate()
    };
}

// Initial State
const state = {
    filters: {
        claimStatus: null,
        claimType: null,
        diseaseOrAccident: null,
        diseaseType: null,
        accidentType: null,
        claimCost: 'all',
        fromDate: null,
        toDate: null,
        reserveValue: null,
        fromReserve: null,
        toReserve: null,
    },
    averageReserve: {
        type: 'value',
        charts: {
            all: chartTemplate(),
            damages: chartTemplate(),
            claimantCosts: chartTemplate(),
            defenceCosts: chartTemplate(),
        },
        running: false
    },
    totalReserve: {
        type: 'value',
        charts: {
            all: chartTemplate(),
            damages: chartTemplate(),
            claimantCosts: chartTemplate(),
            defenceCosts: chartTemplate(),
        },
        running: false
    },
    trendReserve: {
        period: 'month',
        type: 'average',
        charts: {
            all: trendChartTemplate(),
            damages: trendChartTemplate(),
            claimantCosts: trendChartTemplate(),
            defenceCosts: trendChartTemplate(),
        },
        running: false
    },
};


// Getters
const getters = {
    getPeriod: state => key => state[key].period,
    averageReservesChartData: state => averageChartData(state, 'value'),
    totalReservesChartData: state => totalChartData(state, 'value'),
    trendsInAverageReservesChartData: state => trendsChartData(state, 'average'),
    trendsInTotalReservesChartData: state => trendsChartData(state, 'total'),
    caseNumbersChartData: state => totalChartData(state, 'number'),
    caseNumbersTrendChartData: state => trendsChartData(state, 'number'),
    getRunning: state => key => state[key].running,
    diseaseOrAccident: state => state.filters.diseaseOrAccident,
    diseaseType: state => state.filters.diseaseType,
    accidentType: state => state.filters.accidentType,
    claimCost: state => state.filters.claimCost,
    claimStatus: state => state.filters.claimStatus,
    claimType: state => state.filters.claimType,
    fromDate: state => state.filters.fromDate,
    toDate: state => state.filters.toDate,
    reserveValue: state => state.filters.reserveValue,
    fromReserve: state => state.filters.fromReserve,
    toReserve: state => state.filters.toReserve,
    diseaseTypeSelected: state => state.filters.diseaseType !== null,
    accidentTypeSelected: state => state.filters.accidentType !== null,
};

// Actions
const actions = {
    /**
     * Fetch the Average Reserves By Disease
     *
     * @param dispatch
     * @param commit
     * @param data
     */
    averageReserves: _.debounce(({dispatch, commit, state}) => {
        commit(mutationTypes.START_RUNNING, 'averageReserve');

        return Api.dashboards.executive.averageReserves(state.filters)
            .then(data => dispatch('updateAverageReserves', data))
            .then(() => commit(mutationTypes.STOP_RUNNING, 'averageReserve'));
    }, 100),

    /**
     * Fetch the Total Reserves By Disease
     *
     * @param dispatch
     * @param commit
     * @param data
     */
    totalReserves: _.debounce(({dispatch, commit, state}) => {
        commit(mutationTypes.START_RUNNING, 'totalReserve');

        return Api.dashboards.executive.totalReserves(state.filters)
            .then(data => dispatch('updateTotalReserves', data))
            .then(() => commit(mutationTypes.STOP_RUNNING, 'totalReserve'));
    }, 100),

    /**
     * Fetch the Trends in Reserves
     *
     * @param dispatch
     * @param commit
     * @param data
     */
    trendsInReserves: _.debounce(({dispatch, commit, state}) => {
        commit(mutationTypes.START_RUNNING, 'trendReserve');

        return Api.dashboards.executive.trendsInReserves(state.filters)
            .then(data => dispatch('updateTrendsInReserve', data))
            .then(() => commit(mutationTypes.STOP_RUNNING, 'trendReserve'));
    }, 0),

    /**
     * Update the average reserve chart data
     *
     * @param commit
     * @param data
     */
    updateAverageReserves({commit}, data) {
        return commit(mutationTypes.UPDATE_AVERAGE_RESERVES_CHART_DATA, data);
    },

    /**
     * Update the average reserve chart data
     *
     * @param commit
     * @param data
     */
    updateTotalReserves({commit}, data) {
        return commit(mutationTypes.UPDATE_TOTAL_RESERVES_CHART_DATA, data);
    },

    /**
     * Update the trends reserve chart data
     *
     * @param commit
     * @param data
     */
    updateTrendsInReserve({commit}, data) {
        return commit(mutationTypes.UPDATE_TRENDS_IN_RESERVES_CHART_DATA, data);
    },

    /**
     * Update the average reserve chart type
     *
     * @param commit
     * @param data
     */
    updateAverageReservesType({commit}, data) {
        return commit(mutationTypes.UPDATE_AVERAGE_RESERVES_TYPE, data);
    },

    /**
     * Update the total reserve chart type
     *
     * @param commit
     * @param data
     */
    updateTotalReservesType({commit}, data) {
        return commit(mutationTypes.UPDATE_TOTAL_RESERVES_TYPE, data);
    },

    /**
     * Update the trends in reserve chart type
     *
     * @param commit
     * @param data
     */
    updateTrendsInReservesType({commit}, data) {
        return commit(mutationTypes.UPDATE_TRENDS_IN_RESERVES_TYPE, data);
    },

    /**
     * Update the trends in reserve chart period
     *
     * @param commit
     * @param data
     */
    updateTrendsInReservesPeriod({commit}, data) {
        return commit(mutationTypes.UPDATE_TRENDS_IN_RESERVES_PERIOD, data);
    },

    updateAll({dispatch}) {
        return Promise.all([
            dispatch('averageReserves'),
            dispatch('totalReserves'),
            dispatch('trendsInReserves'),
        ]);
    },

    updateFilter({commit, dispatch}, {filter, value}) {
        commit(mutationTypes.UPDATE_FILTER, {filter, value});
        if (filter !== 'claimCost') {
            return dispatch('updateAll');
        }
    }
};

// Mutations
const mutations = {
    [mutationTypes.UPDATE_AVERAGE_RESERVES_CHART_DATA](state, data) {
        state.averageReserve.charts = data;
    },
    [mutationTypes.UPDATE_TOTAL_RESERVES_CHART_DATA](state, data) {
        state.totalReserve.charts = data;
    },
    [mutationTypes.UPDATE_TRENDS_IN_RESERVES_CHART_DATA](state, data) {
        state.trendReserve.charts = data;
    },
    [mutationTypes.UPDATE_AVERAGE_RESERVES_TYPE](state, data) {
        state.averageReserve.type = data;
    },
    [mutationTypes.UPDATE_TOTAL_RESERVES_TYPE](state, data) {
        state.totalReserve.type = data;
    },
    [mutationTypes.UPDATE_TRENDS_IN_RESERVES_TYPE](state, data) {
        state.trendReserve.type = data;
    },
    [mutationTypes.UPDATE_TRENDS_IN_RESERVES_PERIOD](state, data) {
        state.trendReserve.period = data;
    },
    [mutationTypes.START_RUNNING](state, key) {
        state[key].running = true;
    },
    [mutationTypes.STOP_RUNNING](state, key) {
        state[key].running = false;
    },
    [mutationTypes.UPDATE_FILTER](state, {filter, value}) {
        state.filters[filter] = value;
        // if (filter === 'diseaseType' && value !== null) {
        //     state.averageReserve.type = 'value';
        //     state.totalReserve.type = 'value';
        //     state.trendReserve.type = 'average';
        //     state.filters.claimCost = 'all';
        // }
    }
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
    modules: {}
};



// WEBPACK FOOTER //
// ./src/store/modules/dashboards/index.js